import { X, Plus, Trash2, Tag } from 'lucide-react';
import { CLEANING_SERVICES } from '../../utils/invoiceHelpers';

/**
 * Create / Edit Invoice modal. Pure presentation component — all state lives
 * in the parent (Invoices.js); we only call the handlers it passes in.
 *
 * Extracted from Invoices.js as part of the giant-component refactor
 * (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const InvoiceFormModal = ({
  open,
  editingInvoice,
  formData,
  setFormData,
  customers,
  onClose,
  onSubmit,
  onCustomerChange,
  onItemChange,
  onAddItem,
  onAddCleaningService,
  onRemoveItem,
  calculateTotals,
  entityLabel = 'Invoice',
}) => {
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        overflowY: 'auto',
        padding: '2rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {editingInvoice ? `Edit ${entityLabel}` : `Create New ${entityLabel}`}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
            data-testid="close-invoice-modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={onSubmit} style={{ padding: '1.5rem' }}>
          {/* Customer Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Customer *
            </label>
            <select
              value={formData.customerId}
              onChange={(e) => onCustomerChange(e.target.value)}
              required
              className="input"
            >
              <option value="">Select customer...</option>
              {customers.map((customer) => {
                const contact = [customer.contactFirstName, customer.contactLastName].filter(Boolean).join(' ').trim();
                return (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}{contact ? ` — ${contact}` : ''}
                  </option>
                );
              })}
            </select>
            {/* Primary contact chip — a small hint under the dropdown so
                the user can confirm at a glance which person on the
                account they're billing (matches BILL TO on the preview). */}
            {formData.customerContactName && (
              <div
                data-testid="invoice-contact-hint"
                style={{
                  marginTop: '0.5rem',
                  fontSize: '0.78rem',
                  color: '#475569',
                }}
              >
                Attn: <strong style={{ color: '#0f172a' }}>{formData.customerContactName}</strong>
              </div>
            )}
            {/* The customer's saved address / email / phone are pulled
                automatically from the Customers screen and included in
                BILL TO on the invoice + PDF. Edit the customer to change. */}

            {/* Missing-address warning — appears only after a customer is
                selected AND we couldn't find a mailing address for them. */}
            {formData.customerId && !(formData.customerAddress || '').trim() && (
              <div
                data-testid="invoice-missing-address-warning"
                style={{
                  marginTop: '0.6rem',
                  padding: '0.6rem 0.85rem',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: 6,
                  fontSize: '0.8rem',
                  color: '#92400e',
                  lineHeight: 1.4,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: '0.95rem' }}>⚠️</span>
                <span>
                  <strong>No mailing address on file</strong> for this customer.
                  BILL TO on this invoice will only show their name. Open the
                  <em> Customers </em>screen to add an address, phone, and email.
                </span>
              </div>
            )}
          </div>

          {/* Invoice / P.O. numbers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                {entityLabel} number
              </label>
              <input
                type="text"
                data-testid="invoice-number-input"
                value={formData.invoiceNumber || formData.estimateNumber || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  ...(entityLabel === 'Estimate'
                    ? { estimateNumber: e.target.value }
                    : { invoiceNumber: e.target.value }),
                })}
                className="input"
                placeholder={entityLabel === 'Estimate' ? 'EST-YYYY-NNN' : 'INV-YYYY-NNN'}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                P.O./S.O. number
              </label>
              <input
                type="text"
                data-testid="invoice-po-number-input"
                value={formData.poNumber || ''}
                onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                className="input"
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Invoice Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                {entityLabel} Date
              </label>
              <input
                type="date"
                data-testid="invoice-date-input"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                {entityLabel === 'Estimate' ? 'Valid until' : 'Due Date'}
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="input"
              >
                {entityLabel === 'Estimate' ? (
                  <>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </>
                ) : (
                  <>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>Line Items</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Quick Add:</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const service = CLEANING_SERVICES.find((s) => s.description === e.target.value);
                      if (service) onAddCleaningService(service);
                      e.target.value = '';
                    }
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    minWidth: '200px',
                  }}
                >
                  <option value="">Select cleaning service...</option>
                  {CLEANING_SERVICES.map((service) => (
                    <option key={service.description} value={service.description}>
                      {service.description} - ${service.rate}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {formData.items.map((item, index) => (
              <div
                key={item.id || `legacy-${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                }}
              >
                <input
                  type="text"
                  placeholder="Description or select from Quick Add"
                  value={item.description}
                  onChange={(e) => onItemChange(index, 'description', e.target.value)}
                  className="input"
                />
                <input
                  type="number"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                  className="input"
                />
                <input
                  type="number"
                  placeholder="Rate"
                  step="0.01"
                  value={item.rate}
                  onChange={(e) => onItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                  className="input"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={item.amount.toFixed(2)}
                  readOnly
                  className="input"
                  style={{ background: '#f9fafb' }}
                />
                {formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoveItem(index)}
                    className="btn btn-danger"
                    style={{ padding: '0.625rem' }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={onAddItem}
              className="btn btn-secondary"
              style={{ marginTop: '0.5rem' }}
            >
              <Plus size={16} /> Add Item
            </button>
          </div>

          {/* Totals */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Subtotal:</span>
              <span style={{ fontWeight: 600 }}>${formData.subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>Sales Tax:</span>
                <input
                  type="number"
                  data-testid="invoice-tax-rate-input"
                  value={formData.taxRate}
                  step="0.001"
                  min="0"
                  onChange={(e) => {
                    const newTaxRate = parseFloat(e.target.value) || 0;
                    const totals = calculateTotals(
                      formData.items,
                      newTaxRate,
                      formData.discount,
                      formData.depositAmount
                    );
                    setFormData({ ...formData, taxRate: newTaxRate, tax: totals.tax, total: totals.total });
                  }}
                  style={{ width: '72px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
                <span>%</span>
                <span style={{ fontSize: '0.7rem', color: '#94a3b8' }} title="Default is set in Settings → Tax Settings">
                  (default from Settings)
                </span>
              </div>
              <span style={{ fontWeight: 600 }}>${formData.tax.toFixed(2)}</span>
            </div>

            {/* Discount — hidden until the user clicks "Add a discount". */}
            {(formData.discount > 0 || formData._showDiscount) ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Discount:</span>
                  <span style={{ color: '#64748b' }}>$</span>
                  <input
                    type="number"
                    data-testid="invoice-discount-input"
                    value={formData.discount || 0}
                    step="0.01"
                    min="0"
                    onChange={(e) => {
                      const newDiscount = parseFloat(e.target.value) || 0;
                      const totals = calculateTotals(
                        formData.items,
                        formData.taxRate,
                        newDiscount,
                        formData.depositAmount
                      );
                      setFormData({
                        ...formData,
                        discount: newDiscount,
                        subtotal: totals.subtotal,
                        tax: totals.tax,
                        total: totals.total,
                      });
                    }}
                    style={{ width: '100px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                  <button
                    type="button"
                    data-testid="invoice-remove-discount-btn"
                    onClick={() => {
                      const totals = calculateTotals(
                        formData.items,
                        formData.taxRate,
                        0,
                        formData.depositAmount
                      );
                      setFormData({
                        ...formData,
                        discount: 0,
                        _showDiscount: false,
                        subtotal: totals.subtotal,
                        tax: totals.tax,
                        total: totals.total,
                      });
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    remove
                  </button>
                </div>
                <span style={{ fontWeight: 600, color: '#dc2626' }}>
                  −${(Number(formData.discount) || 0).toFixed(2)}
                </span>
              </div>
            ) : (
              <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  data-testid="invoice-add-discount-btn"
                  onClick={() => setFormData({ ...formData, _showDiscount: true })}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#eff6ff';
                    e.currentTarget.style.borderColor = '#93c5fd';
                    e.currentTarget.style.color = '#1d4ed8';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#dbeafe';
                    e.currentTarget.style.color = '#2563eb';
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.85rem',
                    background: '#ffffff',
                    color: '#2563eb',
                    border: '1px dashed #dbeafe',
                    borderRadius: 999,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    letterSpacing: 0.1,
                    transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  <Tag size={14} strokeWidth={2.4} />
                  Add a discount
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #e5e7eb' }}>
              <span style={{ fontWeight: 700, fontSize: '1.125rem' }}>Total:</span>
              <span style={{ fontWeight: 700, fontSize: '1.125rem', color: '#0073ea' }}>
                ${formData.total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Additional Fields */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              style={{ minHeight: '80px' }}
              placeholder="Add any additional notes or details..."
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Payment Terms
            </label>
            <input
              type="text"
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              className="input"
              placeholder="e.g., Payment due within 15 days"
            />
          </div>

          {/* Invoice Footer Note — appears centered at the bottom of the PDF */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                Invoice Footer Note
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Preset:</label>
                <select
                  data-testid="invoice-footer-preset"
                  onChange={(e) => {
                    if (e.target.value) {
                      setFormData({ ...formData, footerNote: e.target.value });
                      e.target.value = '';
                    }
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    minWidth: '220px',
                  }}
                >
                  <option value="">Choose a preset…</option>
                  <option value="Cleaning is my passion!">Cleaning is my passion!</option>
                  <option value="Thank you for supporting my small business!">Thank you for supporting my small business!</option>
                  <option value="Thank you for your business!">Thank you for your business!</option>
                  <option value="We appreciate your trust — see you next time!">We appreciate your trust — see you next time!</option>
                  <option value="A sparkling clean home is our promise.">A sparkling clean home is our promise.</option>
                </select>
              </div>
            </div>
            <input
              type="text"
              data-testid="invoice-footer-note-input"
              value={formData.footerNote || ''}
              onChange={(e) => setFormData({ ...formData, footerNote: e.target.value })}
              className="input"
              placeholder="e.g., Cleaning is my passion!"
            />
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
              Shown at the bottom of the invoice PDF. Leave blank to use the default.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" data-testid="submit-invoice-button">
              {editingInvoice ? 'Update' : 'Create'} {entityLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceFormModal;
